import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { get } from "lodash";
import ServiceProvider from "@client.services/provider";
import { getError } from "@client.utils/errors";

const DEFAULT_DATA = { data: [], load: false, error: null };
const DEFAULT_TOTAL_MEETINGS_COST = {
  amount: 0,
  ...DEFAULT_DATA,
  timeFrame: "day",
};

const initialState = {
  totalMeetingsCost: DEFAULT_TOTAL_MEETINGS_COST,
  topMeetingParticipants: DEFAULT_DATA,
  topTimeConsumingMeetings: DEFAULT_DATA,
  topMeetingTimeParticipants: DEFAULT_DATA,
  timeSpent: DEFAULT_DATA,
};

export const getTopMeetingTimeParticipantsAsync = createAsyncThunk(
  "charts/topMeetingTimeParticipants",
  async (filter, thunkAPI) => {
    thunkAPI.dispatch(setLoadingTopMeetingTimeParticipants(true));
    try {
      const data = await ServiceProvider.Meetings.getTopTimeParticipants(
        filter
      );
      return data;
    } finally {
      thunkAPI.dispatch(setLoadingTopMeetingTimeParticipants(false));
    }
  }
);

export const getTotalMeetingsCostAsync = createAsyncThunk(
  "charts/totalMeetingsCost",
  async ({ filter, isCostIndexPresentation }) => {
    const data = await ServiceProvider.Meetings.getTotalMeetingsCost(
      filter,
      isCostIndexPresentation
    );
    return data;
  }
);

export const getTopMeetingParticipantsAsync = createAsyncThunk(
  "charts/topMeetingParticipants",
  async (filter, thunkAPI) => {
    thunkAPI.dispatch(setLoadingTopMeetingParticipants(true));
    try {
      const data = await ServiceProvider.Meetings.getTopMeetingParticipants(
        filter
      );
      return data;
    } finally {
      thunkAPI.dispatch(setLoadingTopMeetingParticipants(false));
    }
  }
);

export const getTopTimeConsumingMeetingsAsync = createAsyncThunk(
  "charts/topTimeConsumingMeetings",
  async (filter, thunkAPI) => {
    thunkAPI.dispatch(setLoadingTopTimeConsumingMeetings(true));
    try {
      const data = await ServiceProvider.Meetings.getTopTimeConsumingMeetings(
        filter
      );
      return data;
    } finally {
      thunkAPI.dispatch(setLoadingTopTimeConsumingMeetings(false));
    }
  }
);

export const getTimeSpentAsync = createAsyncThunk(
  "charts/timeSpent",
  async (filter, thunkAPI) => {
    thunkAPI.dispatch(setLoadingTimeSpent(true));
    try {
      const data = await ServiceProvider.Meetings.getTimeSpent(filter);
      return data;
    } finally {
      thunkAPI.dispatch(setLoadingTimeSpent(false));
    }
  }
);

export const chartsSlice = createSlice({
  name: "charts",
  initialState,
  reducers: {
    setLoadingTopMeetingTimeParticipants: (state, action) => {
      state.topMeetingTimeParticipants.load = get(action, "payload", false);
    },
    setLoadingTopMeetingParticipants: (state, action) => {
      state.topMeetingParticipants.load = get(action, "payload", false);
    },
    setLoadingTopTimeConsumingMeetings: (state, action) => {
      state.topTimeConsumingMeetings.load = get(action, "payload", false);
    },
    setLoadingTimeSpent: (state, action) => {
      state.timeSpent.load = get(action, "payload", false);
    },
    clearChartsError: (state) => {
      state.totalMeetingsCost.error = null;
      state.topMeetingParticipants.error = null;
      state.topTimeConsumingMeetings.error = null;
      state.timeSpent.error = null;
      state.topMeetingTimeParticipants.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getTopMeetingTimeParticipantsAsync.fulfilled,
        (state, action) => {
          if (action.payload) {
            state.topMeetingTimeParticipants = {
              data: action.payload,
              load: false,
            };
          } else {
            state.topMeetingTimeParticipants = DEFAULT_DATA;
          }
        }
      )
      .addCase(getTopMeetingTimeParticipantsAsync.rejected, (state, action) => {
        state.topMeetingTimeParticipants = {
          ...DEFAULT_DATA,
          error: getError(action),
        };
      })
      .addCase(getTopMeetingParticipantsAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.topMeetingParticipants = {
            data: action.payload,
            load: false,
          };
        } else {
          state.topMeetingParticipants = DEFAULT_DATA;
        }
      })
      .addCase(getTopMeetingParticipantsAsync.rejected, (state, action) => {
        state.topMeetingParticipants = {
          ...DEFAULT_DATA,
          error: getError(action),
        };
      })
      .addCase(getTotalMeetingsCostAsync.pending, (state) => {
        state.totalMeetingsCost.load = true;
      })
      .addCase(getTotalMeetingsCostAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.totalMeetingsCost = {
            data: get(action.payload, "meetings", []),
            amount: get(action.payload, "amount", 0),
            timeFrame: get(action.payload, "timeFrame", "day"),
            load: false,
          };
        } else {
          state.totalMeetingsCost = { ...DEFAULT_TOTAL_MEETINGS_COST };
        }
      })
      .addCase(getTotalMeetingsCostAsync.rejected, (state, action) => {
        state.totalMeetingsCost = {
          ...DEFAULT_TOTAL_MEETINGS_COST,
          error: getError(action),
        };
      })
      .addCase(getTopTimeConsumingMeetingsAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.topTimeConsumingMeetings = {
            data: action.payload,
            load: false,
          };
        } else {
          state.topTimeConsumingMeetings = DEFAULT_DATA;
        }
      })
      .addCase(getTopTimeConsumingMeetingsAsync.rejected, (state, action) => {
        state.topTimeConsumingMeetings = {
          ...DEFAULT_DATA,
          error: getError(action),
        };
      })
      .addCase(getTimeSpentAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.timeSpent = {
            data: action.payload,
            load: false,
          };
        } else {
          state.timeSpent = DEFAULT_DATA;
        }
      })
      .addCase(getTimeSpentAsync.rejected, (state, action) => {
        state.timeSpent = {
          ...DEFAULT_DATA,
          error: getError(action),
        };
      });
  },
});

export const {
  setLoadingTopMeetingTimeParticipants,
  setLoadingTopMeetingParticipants,
  setLoadingTopTimeConsumingMeetings,
  setLoadingTimeSpent,
  setFilter,
  clearChartsError,
} = chartsSlice.actions;

export const makeTopMeetingTimeParticipants = (state) =>
  state.charts.topMeetingTimeParticipants;
export const makeTotalMeetingsCost = (state) => state.charts.totalMeetingsCost;
export const makeTopMeetingParticipants = (state) =>
  state.charts.topMeetingParticipants;
export const makeTopTimeConsumingMeetings = (state) =>
  state.charts.topTimeConsumingMeetings;
export const makeTimeSpent = (state) => state.charts.timeSpent;

export default chartsSlice.reducer;
